import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Flex, Grid, Heading, HStack, Icon, Image, Text, VStack } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiActivity,
  FiCheck,
  FiCheckCircle,
  FiEyeOff,
  FiGlobe,
  FiKey,
  FiLock,
  FiServer,
  FiShield,
  FiSmartphone,
  FiUsers
} from "react-icons/fi";
import { Backdrop, getScene } from "./Scenes";
import { SlideTone, SlideViewData } from "./types";

// Tytuł z akcentem: fragmenty w *gwiazdkach* renderowane kolorem akcentu (dwukolorowy tytuł).
const renderTitleParts = (title: string, accent: string) =>
  title.split(/(\*[^*]+\*)/g).map((p, i) =>
    p.startsWith("*") && p.endsWith("*") ? (
      <Box as="span" key={i} color={accent}>
        {p.slice(1, -1)}
      </Box>
    ) : (
      <Box as="span" key={i}>
        {p}
      </Box>
    )
  );

// Stała „kanwa" 16:9 skalowana do okna — identyczny layout na rzutniku, telewizorze i laptopie.
export const STAGE_W = 1280;
export const STAGE_H = 720;

const toneFg = (tone?: SlideTone) => (tone === "light" || !tone ? "#1A1A2E" : "#FFFFFF");
const toneMuted = (tone?: SlideTone) => (tone === "light" || !tone ? "#5B5B6B" : "rgba(255,255,255,0.85)");
const toneAccent = (tone?: SlideTone) => (tone === "light" || !tone ? "#430098" : "#FFD23F");

const PILLAR_ICONS: Record<string, any> = {
  FiLock,
  FiEyeOff,
  FiShield,
  FiCheck,
  FiCheckCircle,
  FiUsers,
  FiActivity,
  FiServer,
  FiGlobe,
  FiSmartphone,
  FiKey
};

export const SlideStage: FC<{ tone?: SlideTone; children: ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      setScale(Math.min(width / STAGE_W, height / STAGE_H) || 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <Box ref={ref} position="absolute" inset={0} overflow="hidden" bg="#0B0618">
      <Box
        position="absolute"
        top="50%"
        left="50%"
        width={`${STAGE_W}px`}
        height={`${STAGE_H}px`}
        transform={`translate(-50%, -50%) scale(${scale})`}
        transformOrigin="center center"
        overflow="hidden"
      >
        {children}
      </Box>
    </Box>
  );
};

const StatCounter: FC<{ value: string; reduced?: boolean }> = ({ value, reduced }) => {
  const match = value.match(/^(\D*)(\d[\d\s.,]*)(.*)$/);
  const [shown, setShown] = useState(reduced || !match ? value : `${match[1]}0${match[3]}`);

  useEffect(() => {
    if (reduced || !match) {
      setShown(value);
      return;
    }
    const target = parseFloat(match[2].replace(/\s/g, "").replace(",", "."));
    if (isNaN(target)) {
      setShown(value);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 900);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(`${match[1]}${Math.round(target * eased)}${match[3]}`);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, reduced]);

  return <>{shown}</>;
};

const MotionStack = motion(VStack) as any;
const MotionDiv = motion.div;

// Treść slajdu: animowane tło + dwie kolumny (tekst + scena SVG) albo pełna szerokość dla filarów.
// Przekładka rozdziału — wyśrodkowany numer + tytuł, bez sceny.
const DividerLayout: FC<{ slide: SlideViewData }> = ({ slide }) => (
  <Box position="absolute" inset={0}>
    <Backdrop tone={slide.tone || "brand"} />
    <Flex position="absolute" inset={0} direction="column" align="center" justify="center" textAlign="center" px="80px">
      <Heading fontSize="62px" fontWeight="900" color="#fff" maxW="960px" lineHeight="1.06" letterSpacing="-0.8px">
        {renderTitleParts(slide.title || "", "#FFD23F")}
      </Heading>
      <Box mt="26px" w="88px" h="5px" borderRadius="full" bg="#E51F44" />
      {slide.lead && (
        <Text mt="24px" fontSize="22px" fontWeight="500" color="rgba(255,255,255,0.85)" maxW="780px" lineHeight="1.45">
          {slide.lead}
        </Text>
      )}
    </Flex>
  </Box>
);

export const SlideContent: FC<{ slide: SlideViewData; reduced?: boolean; customScenes?: Record<string, ReactNode> }> = ({
  slide,
  reduced,
  customScenes
}) => {
  if (slide.divider) return <DividerLayout slide={slide} />;
  const fg = toneFg(slide.tone);
  const muted = toneMuted(slide.tone);
  const accent = toneAccent(slide.tone);
  const dark = slide.tone === "dark" || slide.tone === "brand";

  const scene = slide.pillars ? null : customScenes?.[slide.scene ?? ""] ?? getScene(slide.scene, slide.tone);
  const hasScene = !!scene;

  const container = { animate: { transition: { staggerChildren: reduced ? 0 : 0.09, delayChildren: 0.06 } } };
  const item = reduced
    ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
    : { initial: { opacity: 0, y: 22 }, animate: { opacity: 1, y: 0 } };

  const statCols = hasScene ? 2 : Math.min(slide.stats?.length || 1, 4);

  return (
    <Box position="absolute" inset={0}>
      <Backdrop tone={slide.tone} />

      <Flex position="absolute" inset={0} align="center" px="72px" py="56px" gap="44px">
        <Box flex={hasScene ? "1.05" : "1"} minW={0}>
          <MotionStack align="flex-start" spacing="20px" variants={container} initial="initial" animate="animate">
            {slide.logo && (
              <MotionDiv variants={item}>
                <Image src={slide.logo} alt="logo" h="48px" mb="4px" />
              </MotionDiv>
            )}

            {slide.kicker && (
              <MotionDiv variants={item}>
                <HStack spacing="10px">
                  <Box w="26px" h="4px" borderRadius="full" bg={accent} />
                  <Text textTransform="uppercase" letterSpacing="0.18em" fontWeight="800" fontSize="18px" color={accent}>
                    {slide.kicker}
                  </Text>
                </HStack>
              </MotionDiv>
            )}

            {slide.title && (
              <MotionDiv variants={item}>
                <Heading as="h1" fontSize="58px" lineHeight="1.06" fontWeight="900" color={fg} maxW="900px" letterSpacing="-0.6px">
                  {renderTitleParts(slide.title, dark ? "#FFD23F" : "#E51F44")}
                </Heading>
                <Box mt="20px" w="70px" h="5px" borderRadius="full" bg="#E51F44" />
              </MotionDiv>
            )}

            {slide.lead && (
              <MotionDiv variants={item}>
                <Text fontSize="28px" lineHeight="1.45" fontWeight="500" color={dark ? "rgba(255,255,255,0.92)" : "#3A3A48"} maxW="860px">
                  {slide.lead}
                </Text>
              </MotionDiv>
            )}

            {slide.bullets && (
              <VStack align="flex-start" spacing="17px" pt="8px">
                {slide.bullets.map((b, i) => (
                  <MotionDiv key={i} variants={item}>
                    <HStack align="flex-start" spacing="16px">
                      <Flex mt="2px" w="32px" h="32px" borderRadius="full" align="center" justify="center" flexShrink={0}
                        bg={dark ? "rgba(255,210,63,0.16)" : "#FCE7EC"}>
                        <Icon as={FiCheck} boxSize="19px" color={dark ? "#FFD23F" : "#E51F44"} />
                      </Flex>
                      <Text fontSize="24px" lineHeight="1.42" fontWeight="500" color={fg} maxW="800px">
                        {b}
                      </Text>
                    </HStack>
                  </MotionDiv>
                ))}
              </VStack>
            )}

            {slide.stats && (
              <Grid templateColumns={`repeat(${statCols}, 1fr)`} gap="20px" pt="12px" w="100%">
                {slide.stats.map((s, i) => (
                  <MotionDiv key={i} variants={item}>
                    <VStack align="flex-start" spacing="4px">
                      <Heading as="div" fontSize="58px" fontWeight="900" color={accent} lineHeight="1">
                        <StatCounter value={s.value} reduced={reduced} />
                      </Heading>
                      <Text fontSize="19px" lineHeight="1.3" color={muted}>
                        {s.label}
                      </Text>
                    </VStack>
                  </MotionDiv>
                ))}
              </Grid>
            )}

            {slide.terms && (
              <MotionDiv variants={item}>
                <Flex flexWrap="wrap" gap="10px" pt="6px" align="center">
                  <Text fontSize="13px" fontWeight="700" color={muted} mr="2px">
                    po technicznemu:
                  </Text>
                  {slide.terms.map((t, i) => (
                    <Flex key={i} align="center" gap="7px" borderRadius="full" px="13px" py="6px"
                      bg={dark ? "rgba(255,255,255,0.09)" : "#F2ECFB"}
                      border="1px solid" borderColor={dark ? "rgba(255,255,255,0.16)" : "#E4D8F7"}>
                      <Text fontSize="14px" fontWeight="800" color={dark ? "#FFD23F" : "#430098"}>
                        {t.term}
                      </Text>
                      <Text fontSize="13px" color={muted}>· {t.gloss}</Text>
                    </Flex>
                  ))}
                </Flex>
              </MotionDiv>
            )}

            {slide.cta && (
              <MotionDiv variants={item}>
                <Box mt="12px" bg="#E51F44" color="#fff" px="28px" py="15px" borderRadius="full" fontSize="22px" fontWeight="700" boxShadow="0 10px 30px rgba(229,31,68,0.35)">
                  {slide.cta}
                </Box>
              </MotionDiv>
            )}
          </MotionStack>

          {slide.pillars && (
            <Grid templateColumns={`repeat(${slide.pillars.length}, 1fr)`} gap="22px" pt="30px" w="100%">
              {slide.pillars.map((p, i) => {
                const PIcon = p.icon ? PILLAR_ICONS[p.icon] : null;
                return (
                  <MotionDiv
                    key={i}
                    initial={reduced ? { opacity: 1 } : { opacity: 0, y: 26 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.14, duration: 0.5 }}
                  >
                    <VStack
                      align="flex-start"
                      spacing="14px"
                      bg={dark ? "rgba(255,255,255,0.08)" : "#FFFFFF"}
                      border="1px solid"
                      borderColor={dark ? "rgba(255,255,255,0.16)" : "#E6E1F2"}
                      borderRadius="20px"
                      p="26px"
                      h="100%"
                      boxShadow={dark ? "none" : "0 16px 40px rgba(67,0,152,0.10)"}
                    >
                      <Flex w="58px" h="58px" borderRadius="16px" align="center" justify="center" bg={dark ? "rgba(255,210,63,0.18)" : "#F1ECFB"}>
                        {PIcon ? <Icon as={PIcon} boxSize="30px" color={accent} /> : <Box w="26px" h="6px" bg={accent} borderRadius="full" />}
                      </Flex>
                      <Heading as="h3" fontSize="27px" fontWeight="800" color={fg}>
                        {p.title}
                      </Heading>
                      <Text fontSize="20px" lineHeight="1.42" color={muted}>
                        {p.text}
                      </Text>
                    </VStack>
                  </MotionDiv>
                );
              })}
            </Grid>
          )}
        </Box>

        {hasScene && (
          <Box flex="0.95" position="relative" alignSelf="stretch" minW={0}>
            {scene}
          </Box>
        )}
      </Flex>

      {!slide.logo && (
        <Text
          position="absolute"
          bottom="34px"
          left="72px"
          fontSize="15px"
          fontWeight="800"
          letterSpacing="-0.2px"
          color={dark ? "rgba(255,255,255,0.5)" : "rgba(67,0,152,0.48)"}
        >
          lubbezpośrednio.pl
        </Text>
      )}
    </Box>
  );
};

export const DeckEngine: FC<{
  slide: SlideViewData;
  direction: 1 | -1;
  reduced?: boolean;
  customScenes?: Record<string, ReactNode>;
}> = ({ slide, direction, reduced, customScenes }) => {
  const variants = reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: (d: number) => ({ opacity: 0, x: d * 90 }),
        animate: { opacity: 1, x: 0 },
        exit: (d: number) => ({ opacity: 0, x: -d * 90 })
      };

  return (
    <SlideStage tone={slide.tone}>
      <AnimatePresence custom={direction} initial={false}>
        <motion.div
          key={slide.id}
          custom={direction}
          variants={variants as any}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: reduced ? 0.25 : 0.5, ease: "easeOut" }}
          style={{ position: "absolute", inset: 0 }}
        >
          <SlideContent slide={slide} reduced={reduced} customScenes={customScenes} />
        </motion.div>
      </AnimatePresence>
    </SlideStage>
  );
};

// Wersja do druku / PDF: wszystkie slajdy statycznie. Każdy slajd = jedna kartka o własnym
// rozmiarze 16:9 (13,333×7,5 cala = 1280×720 px @96dpi), kanwa wypełnia całą stronę — bez letterboxa.
// Rozmiar kartki w @media print (PRINT_CSS w widoku). Zapis przez „Drukuj → Zapisz jako PDF".
export const PrintDeck: FC<{
  slides: SlideViewData[];
  customScenes?: Record<string, ReactNode>;
}> = ({ slides, customScenes }) => (
  <Box className="print-deck">
    {slides.map((s) => (
      <Box key={s.id} className="print-slide">
        <Box className="print-stage" position="relative" width={`${STAGE_W}px`} height={`${STAGE_H}px`} overflow="hidden" bg="#0B0618">
          <SlideContent slide={s} reduced customScenes={customScenes} />
        </Box>
      </Box>
    ))}
  </Box>
);

export const ProgressBar: FC<{ progress: number }> = ({ progress }) => (
  <Box position="absolute" bottom="0" left="0" right="0" h="4px" bg="rgba(255,255,255,0.12)" zIndex={5}>
    <Box h="100%" bg="#E51F44" width={`${Math.round(progress * 100)}%`} transition="width 0.4s ease" />
  </Box>
);

export const SlideNav: FC<{
  index: number;
  total: number;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onOverview?: () => void;
}> = ({ index, total, canPrev, canNext, onPrev, onNext, onOverview }) => (
  <HStack
    position="absolute"
    bottom="18px"
    right="22px"
    spacing="10px"
    bg="rgba(255,255,255,0.92)"
    borderRadius="full"
    px="12px"
    py="8px"
    boxShadow="0 4px 18px rgba(0,0,0,0.18)"
    zIndex={6}
  >
    <NavBtn label="‹" onClick={onPrev} disabled={!canPrev} />
    <Text fontSize="14px" fontWeight="700" color="#430098" minW="58px" textAlign="center">
      {index + 1} / {total}
    </Text>
    <NavBtn label="›" onClick={onNext} disabled={!canNext} />
    {onOverview && <NavBtn label="▦" onClick={onOverview} />}
  </HStack>
);

const NavBtn: FC<{ label: string; onClick: () => void; disabled?: boolean }> = ({ label, onClick, disabled }) => (
  <Box
    as="button"
    onClick={onClick}
    aria-disabled={disabled}
    opacity={disabled ? 0.35 : 1}
    cursor={disabled ? "not-allowed" : "pointer"}
    w="34px"
    h="34px"
    borderRadius="full"
    bg="#F5F2FB"
    color="#430098"
    fontSize="20px"
    fontWeight="800"
    lineHeight="34px"
  >
    {label}
  </Box>
);

export const OverviewGrid: FC<{
  slides: SlideViewData[];
  currentIndex: number;
  onSelect: (i: number) => void;
}> = ({ slides, currentIndex, onSelect }) => (
  <Box position="absolute" inset={0} bg="#140A2B" overflowY="auto" p="32px">
    <Grid templateColumns="repeat(auto-fill, minmax(260px, 1fr))" gap="18px">
      {slides.map((s, i) => (
        <Box
          key={s.id}
          as="button"
          onClick={() => onSelect(i)}
          textAlign="left"
          borderRadius="14px"
          border="2px solid"
          borderColor={i === currentIndex ? "#E51F44" : "rgba(255,255,255,0.16)"}
          bg="rgba(255,255,255,0.06)"
          p="16px"
          minH="140px"
        >
          <Text fontSize="12px" fontWeight="800" color="#FFD23F" textTransform="uppercase" letterSpacing="0.1em">
            {i + 1}. {s.kicker || ""}
          </Text>
          <Text mt="6px" fontSize="17px" fontWeight="700" color="#fff" noOfLines={3}>
            {s.title || s.lead || s.id}
          </Text>
        </Box>
      ))}
    </Grid>
  </Box>
);
